import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { UI } from "@/i18n";
import { fileUrl } from "@/lib/api";
import { scrollToId } from "@/App";

const LINKS = [
  { id: "services", key: "services" },
  { id: "work", key: "gallery" },
  { id: "reviews", key: "reviews" },
  { id: "contact", key: "contact" },
];

export default function Navbar() {
  const { content, t, lang, toggleLang } = useSite();
  const [open, setOpen] = useState(false);
  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), 50);
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div className="hidden sm:flex items-center justify-between px-6 py-1.5 bg-brand-cobalt text-[11px] font-mono2 tracking-wider uppercase">
        <span data-testid="topbar-area">{t(content?.service_area)}</span>
        <a href={`tel:${content?.phone}`} data-testid="topbar-phone" className="font-semibold hover:text-white text-white/90">
          {content?.phone} - {t(content?.hours)}
        </a>
      </div>
      <nav className="bg-brand-navy/85 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <button onClick={() => go("top")} data-testid="nav-logo" className="flex items-center gap-3 min-w-0">
            <img
              src={fileUrl(content?.logo_url)}
              alt="CT Upholstery logo"
              className="h-10 w-auto rounded-md border border-white/15 bg-brand-cobalt"
            />
            <span className="hidden md:block leading-none text-left">
              <span className="font-display text-lg tracking-wide block">CT UPHOLSTERY</span>
              <span className="text-[10px] font-mono2 uppercase tracking-[0.25em] text-brand-orange">
                {t(content?.site_tagline)}
              </span>
            </span>
          </button>

          <div className="hidden lg:flex items-center gap-7">
            {LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                data-testid={`nav-link-${l.id}`}
                className="text-sm font-medium text-white/80 hover:text-white transition-colors relative group"
              >
                {t(UI.nav[l.key])}
                <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-brand-orange transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={toggleLang}
              data-testid="lang-toggle"
              className="font-mono2 text-xs font-semibold px-3 py-2 rounded-full border border-white/20 hover:border-brand-orange hover:text-brand-orange transition-colors"
              aria-label="Switch language"
            >
              <span className={lang === "en" ? "text-brand-orange" : "text-white/60"}>EN</span>
              <span className="text-white/30 mx-1">|</span>
              <span className={lang === "es" ? "text-brand-orange" : "text-white/60"}>ES</span>
            </button>
            <a
              href={`tel:${content?.phone}`}
              data-testid="nav-call-btn"
              className="hidden md:inline-flex items-center gap-2 bg-brand-red hover:bg-red-600 text-white font-semibold text-sm px-4 py-2.5 rounded-full transition-all hover:scale-[1.03]"
            >
              <Phone size={15} strokeWidth={2.5} />
              <span className="font-mono2">{content?.phone}</span>
            </a>
            <button
              onClick={() => setOpen(!open)}
              data-testid="nav-menu-toggle"
              className="lg:hidden p-2 text-white/80"
              aria-label={t(UI.nav.menu)}
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="lg:hidden overflow-hidden border-t border-white/10 bg-brand-navy/95 backdrop-blur-md"
            >
              <div className="px-6 py-4 flex flex-col gap-1">
                {LINKS.map((l) => (
                  <button
                    key={l.id}
                    onClick={() => go(l.id)}
                    className="text-left py-3 border-b border-white/5 font-medium text-white/85"
                  >
                    {t(UI.nav[l.key])}
                  </button>
                ))}
                <a
                  href={`tel:${content?.phone}`}
                  className="mt-3 inline-flex items-center justify-center gap-2 bg-brand-red text-white font-semibold px-4 py-3 rounded-full"
                >
                  <Phone size={16} /> <span className="font-mono2">{content?.phone}</span>
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
}
